<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form v-if="render">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('testimonials.name')"
            label-for="testimonial-name"
          >
            <b-form-input
              id="testimonial-name"
              v-model="form.name"
              :state="validateState('name')"
            />
            <b-form-invalid
              id="input-name-feedback"
              :label="$t('testimonials.name')"
              :validator="$v.form.name"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="testimonial-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('testimonials.priority')"
            label-for="testimonial-priority"
          >
            <b-form-input
              id="testimonial-priority"
              v-model="form.priority"
              :state="validateState('priority')"
            />
            <b-form-invalid
              id="input-priority-feedback"
              :label="$t('testimonials.priority')"
              :validator="$v.form.priority"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            :label="$t('testimonials.isActive')"
            label-for="testimonial-isActive"
            class="mb-2"
          >
            <b-form-checkbox
              id="testimonial-isActive"
              v-model="form.isActive"
              class="mt-1"
              name="testimonial-isActive"
              switch
              inline
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="testimonial-content"
            class="mb-2"
          >
            <CkeditorBasic
              id="testimonial-content"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="testimonialImage"
            v-model="form.imageFileId"
            :label="$t('general.image') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('selectFile')"
            :state="validateState('imageFileId')"
            :alt.sync="form.imageFileIdAlt"
            :alt-validator="$v.form.imageFileIdAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-testimonial-image-feedback"
                :label="$t('general.image')"
                :validator="$v.form.imageFileId"
              />
            </template>
          </b-file-input>
        </b-col>

        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormCheckbox, BFormGroup, BFormInput, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import '@/components/VSelectCustom/index.vue'
import languageSelector from '@/components/language-selector.vue'
import { required, minLength, integer } from 'vuelidate/lib/validators'
import useTestimonialsList from './useTestimonialsList'

export default {
  name: 'TestimonialEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        name: {
          required,
          minLength: minLength(3),
        },
        content: {
          required,
          minLength: minLength(10),
        },
        isActive: {},
        imageFileId: {},
        imageFileIdAlt: {
          minLength: minLength(3),
        },
        priority: {
          integer,
        },
      },
    }
  },
  setup() {
    const {
      convertResource,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      updateItem,
    } = useTestimonialsList()
    return {
      convertResource,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  created() {
    if (this.itemId) {
      this.fetchItem(this.itemId)
    }
  },
  methods: {
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(this.item, selectedLanguage)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['imageFileId']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        name: form.name,
        content: form.content,
        isActive: form.isActive,
        imageFileId: form.imageFileId ? form.imageFileId._id : null,
        priority: parseInt(form.priority, 10),
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
